import { inverseSquare, LineBuffer, Particles, makeOrb, removeObj, tint, clamp } from "../shared";

class Magnetic {
  constructor(ctx) {
    this.ctx = ctx; this.writesColors = true; this.poles = []; this.maxB = 0;
    this.lines = new LineBuffer(ctx.overlay, 9000, 0.8);
    this.parts = new Particles(ctx.overlay, ctx.lowPower ? 400 : 1500, 0xbfefff, ctx.lowPower ? 0.08 : 0.05); this.frame = 0;
    for (let i = 0; i < this.parts.count; i++) this.respawn(i);
    this.preset("dipole");
  }
  addPole(x, z, q) { const p = { x, z, q, vx: 0, vz: 0, mesh: makeOrb(this.ctx.overlay, q > 0 ? 0x00f0ff : 0xff3d6e, 0.22) }; this.poles.push(p); return p; }
  clearPoles() { this.poles.forEach((p) => removeObj(this.ctx.overlay, p.mesh)); this.poles = []; }
  preset(k) {
    this.clearPoles();
    if (k === "dipole") { this.addPole(-2, 0, 1); this.addPole(2, 0, -1); }
    if (k === "quad") [[-2, -2, 1], [2, -2, -1], [2, 2, 1], [-2, 2, -1]].forEach(([x, z, q]) => this.addPole(x, z, q));
    if (k === "random") for (let i = 0; i < 6; i++) this.addPole((Math.random() - 0.5) * 8, (Math.random() - 0.5) * 8, i % 2 ? -1 : 1);
  }
  respawn(i) {
    const h = this.ctx.half, pp = this.parts.pos;
    pp[i * 3] = (Math.random() * 2 - 1) * h; pp[i * 3 + 2] = (Math.random() * 2 - 1) * h; this.parts.age[i] = Math.random() * 4;
  }
  pointer(e) {
    if (e.type === "down") {
      let best = null, bd = 0.6;
      for (const p of this.poles) { const d = Math.hypot(p.x - e.x, p.z - e.z); if (d < bd) { bd = d; best = p; } }
      if (!best && this.poles.length < 14) best = this.addPole(e.x, e.z, (this.ctx.params.polarity === "N" ? 1 : -1) * (e.shift ? -1 : 1));
      this.drag = best;
    } else if (e.type === "move" && this.drag) { this.drag.x = e.x; this.drag.z = e.z; this.drag.vx = this.drag.vz = 0; }
    else if (e.type === "up") this.drag = null;
  }
  dynamics(dt) {
    const P = this.ctx.params, K = 1.5 * P.strength * this.ctx.g.force, ps = this.poles, lim = this.ctx.half - 0.3;
    for (let i = 0; i < ps.length; i++) {
      const a = ps[i]; if (a === this.drag) continue;
      let ax = 0, az = 0;
      for (let j = 0; j < ps.length; j++) {
        if (i === j) continue;
        const b = ps[j], dx = a.x - b.x, dz = a.z - b.z, r2 = Math.max(0.2, dx * dx + dz * dz), r = Math.sqrt(r2);
        if (r < 0.45 && a.q * b.q < 0) { a.vx *= 0.5; a.vz *= 0.5; continue; }
        const f = (K * a.q * b.q) / (r2 * r); ax += dx * f; az += dz * f;
      }
      a.vx = (a.vx + ax * dt) * (1 - Math.min(0.9, P.damping * dt * 4)); a.vz = (a.vz + az * dt) * (1 - Math.min(0.9, P.damping * dt * 4));
      a.x += a.vx * dt; a.z += a.vz * dt;
      if (Math.abs(a.x) > lim) { a.x = clamp(a.x, -lim, lim); a.vx *= -0.5; }
      if (Math.abs(a.z) > lim) { a.z = clamp(a.z, -lim, lim); a.vz *= -0.5; }
    }
  }
  traceLines() {
    const P = this.ctx.params, L = this.lines, h = this.ctx.half; L.begin();
    if (!P.lines || !this.poles.length) return L.end();
    const pos = this.poles.filter((p) => p.q > 0), srcs = pos.length ? pos : this.poles, dir = pos.length ? 1 : -1;
    for (const s of srcs) for (let k = 0; k < P.lineCount; k++) {
      const a = (k / P.lineCount) * Math.PI * 2;
      let x = s.x + Math.cos(a) * 0.25, z = s.z + Math.sin(a) * 0.25, y = this.ctx.sampleY(x, z) + 0.12;
      for (let st = 0, steps = this.ctx.lowPower ? 90 : 220; st < steps; st++) {
        const [fx, fz] = inverseSquare(this.poles, x, z, 0.05), m = Math.hypot(fx, fz) || 1;
        const nx = x + (fx / m) * 0.09 * dir, nz = z + (fz / m) * 0.09 * dir, ny = this.ctx.sampleY(nx, nz) + 0.12;
        const t = st / steps; L.seg(x, y, z, nx, ny, nz, 0.1 + t * 0.9, 0.9 - t * 0.5, 1 - t * 0.6);
        x = nx; z = nz; y = ny;
        if (Math.abs(x) > h || Math.abs(z) > h) break;
        if (this.poles.some((p) => p.q * dir < 0 && Math.hypot(p.x - x, p.z - z) < 0.2)) break;
      }
    }
    L.end();
  }
  step(dt) {
    const P = this.ctx.params, { N, px, target, colors } = this.ctx, f = P.strength * this.ctx.g.force;
    if (P.dynamics) this.dynamics(dt);
    for (const p of this.poles) p.mesh.position.set(p.x, this.ctx.top + 0.35, p.z);
    let maxB = 0;
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      const i = r * N + c, [fx, fz, pot] = inverseSquare(this.poles, px[c], px[r], 0.35), m = Math.hypot(fx, fz);
      if (m > maxB) maxB = m;
      target[i] = P.view === "potential" ? Math.tanh(pot * 0.6 * f) : Math.tanh(m * 0.35 * f) * 2 - 1;
      tint(colors, i, Math.tanh(pot * 0.8), 0.85);
    }
    this.maxB = maxB; if (this.frame++ % (this.ctx.lowPower ? 6 : 2) === 0 || this.drag) this.traceLines();
    const pa = this.parts; pa.visible = P.filings && this.poles.length > 0;
    if (pa.obj.visible) for (let i = 0; i < pa.count; i++) {
      const x = pa.pos[i * 3], z = pa.pos[i * 3 + 2], [fx, fz] = inverseSquare(this.poles, x, z, 0.1), m = Math.hypot(fx, fz) || 1;
      const sp = Math.min(3, 0.6 + m * 0.5) * dt;
      pa.pos[i * 3] = x + (fx / m) * sp; pa.pos[i * 3 + 2] = z + (fz / m) * sp; pa.pos[i * 3 + 1] = this.ctx.sampleY(x, z) + 0.14;
      pa.age[i] -= dt;
      if (pa.age[i] < 0 || Math.abs(x) > this.ctx.half || Math.abs(z) > this.ctx.half || this.poles.some((p) => p.q < 0 && Math.hypot(p.x - x, p.z - z) < 0.2)) this.respawn(i);
    }
    pa.update();
  }
  action(id) { this.ctx.pushHistory(); this.preset(id); }
  snapshot() { return this.poles.map(({ x, z, q }) => ({ x, z, q })); }
  restore(s) { this.clearPoles(); s.forEach((p) => this.addPole(p.x, p.z, p.q)); }
  clear() { this.clearPoles(); }
  telemetry() {
    const n = this.poles.filter((p) => p.q > 0).length, s = this.poles.length - n;
    return [["Poles", `${n} N / ${s} S`], ["Net charge", `${n - s > 0 ? "+" : ""}${n - s}`], ["Peak |B|", this.maxB.toFixed(2)], ["Dynamics", this.ctx.params.dynamics ? "ON" : "OFF"]];
  }
  dispose() { this.clearPoles(); this.lines.dispose(); this.parts.dispose(); }
}

const magnetic = {
  id: "magnetic-field", name: "Magnetic Field", icon: "Magnet", rest: 0,
  desc: "Click to place magnetic poles, drag to move them. Like poles repel and opposites attract. Pins follow the scalar magnetic potential; glowing lines and iron filings trace the field from North to South.",
  math: "B = Σ qᵢ r̂ / r²   ·   φ = Σ qᵢ / r   ·   F = k q₁q₂ / r²",
  params: [
    { key: "polarity", label: "New pole polarity", type: "select", options: [["N", "North (+)"], ["S", "South (−)"]], def: "N" },
    { key: "strength", label: "Field strength", type: "range", min: 0.2, max: 3, step: 0.05, def: 1.2 },
    { key: "view", label: "Pin readout", type: "select", options: [["potential", "Potential"], ["magnitude", "|B| magnitude"]], def: "potential" },
    { key: "dynamics", label: "Attraction / repulsion motion", type: "toggle", def: true },
    { key: "damping", label: "Friction", type: "range", min: 0, max: 1, step: 0.01, def: 0.4 },
    { key: "lines", label: "Field lines", type: "toggle", def: true },
    { key: "lineCount", label: "Lines per pole", type: "range", min: 6, max: 24, step: 2, def: 12 },
    { key: "filings", label: "Iron filings", type: "toggle", def: true },
  ],
  actions: [{ id: "dipole", label: "Dipole" }, { id: "quad", label: "Quadrupole" }, { id: "random", label: "Random poles" }],
  create: (ctx) => new Magnetic(ctx),
};
export default magnetic;
